/**
 * Copyright (C) 2024 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import { GOOGLE_MAP_INSTANCE_KEY } from '../../components/common/map/GoogleMapSingleton';
import { FlightLegInfo } from '../../shared/entities/flightLeg';
import ActionCreator from '../action-creator';
import { FlightLegUploadSuccessResponse } from './reducer';
interface GetFlightLegsActionProps {
  missionPlanId: number;
  datapathUuid: string | undefined;
  userId: string;
}
interface FlightLegProps {
  missionPlanId: number;
  userId: string;
}

interface UploadFileProps {
  missionPlanId: number;
  userId: string;
  files: File[];
}
interface FlightLegOrderProps {
  missionPlanId: number;
  userId: string;
  idList: string[];
}

interface FlightLegDeleteProps {
  userId: string;
  flightLegId: number;
}

interface DeleteLegConfirmationProps {
  visible: boolean;
}

interface SetFlightLegToDeleteProps {
  flightLeg: FlightLegInfo | undefined;
}

interface MapAction {
  mapId: string;
}
interface SetMissionDetailsOnMap extends MapAction {
  id?: number;
}
interface UpdateFlightLegActionPayload {
  flightLegId: string;
  takeoffTimeNumberOfMinutes: number;
  missionPlanId: number;
}
export const ActionCreators = {
  RequestFlightLegs: new ActionCreator<'RequestFlightLegs', GetFlightLegsActionProps>('RequestFlightLegs'),
  ReceivedFlightLegsSuccess: new ActionCreator<'ReceivedFlightLegsSuccess', any>('ReceivedFlightLegsSuccess'),

  ReceivedFlightLegsError: new ActionCreator<'ReceivedFlightLegsError', any>('ReceivedFlightLegsError'),
  ClearReceivedFlightLegsError: new ActionCreator<'ClearReceivedFlightLegsError', any>('ClearReceivedFlightLegsError'),

  RequestFlightLegsInfo: new ActionCreator<'RequestFlightLegsInfo', FlightLegProps>('RequestFlightLegsInfo'),
  ReceivedFlightLegsInfoSuccess: new ActionCreator<'ReceivedFlightLegsInfoSuccess', any>(
    'ReceivedFlightLegsInfoSuccess',
  ),
  ClearFlightLegsInfo: new ActionCreator<'ClearFlightLegsInfo', any>('ClearFlightLegsInfo'),
  ReceivedFlightLegsInfoError: new ActionCreator<'ReceivedFlightLegsInfoError', any>('ReceivedFlightLegsInfoError'),
  ClearReceivedFlightLegsInfoSuccess: new ActionCreator<'ClearReceivedFlightLegsInfoSuccess', any>(
    'ClearReceivedFlightLegsInfoSuccess',
  ),
  ClearReceivedFlightLegsInfoError: new ActionCreator<'ClearReceivedFlightLegsInfoError', any>(
    'ClearReceivedFlightLegsInfoError',
  ),

  RequestFlightLegUpload: new ActionCreator<'RequestFlightLegUpload', UploadFileProps>('RequestFlightLegUpload'),
  ReceivedFlightLegUploadSuccess: new ActionCreator<'ReceivedFlightLegUploadSuccess', any>(
    'ReceivedFlightLegUploadSuccess',
  ),
  ReceivedFlightLegUploadError: new ActionCreator<'ReceivedFlightLegUploadError', any>('ReceivedFlightLegUploadError'),
  ClearReceivedFlightLegUploadError: new ActionCreator<'ClearReceivedFlightLegUploadError', any>(
    'ClearReceivedFlightLegUploadError',
  ),

  RequestFlightLegOrder: new ActionCreator<'RequestFlightLegOrder', FlightLegOrderProps>('RequestFlightLegOrder'),
  ReceivedFlightLegOrderSuccess: new ActionCreator<'ReceivedFlightLegOrderSuccess', any>(
    'ReceivedFlightLegOrderSuccess',
  ),
  ReceivedFlightLegOrderError: new ActionCreator<'ReceivedFlightLegOrderError', any>('ReceivedFlightLegOrderError'),
  ClearReceivedFlightLegOrderError: new ActionCreator<'ClearReceivedFlightLegOrderError', any>(
    'ClearReceivedFlightLegOrderError',
  ),

  RequestFlightLegDelete: new ActionCreator<'RequestFlightLegDelete', FlightLegDeleteProps>('RequestFlightLegDelete'),
  ReceivedFlightLegDeleteSuccess: new ActionCreator<'ReceivedFlightLegDeleteSuccess', any>(
    'ReceivedFlightLegDeleteSuccess',
  ),
  ReceivedFlightLegDeleteError: new ActionCreator<'ReceivedFlightLegDeleteError', any>('ReceivedFlightLegDeleteError'),
  ClearReceivedFlightLegDeleteStatus: new ActionCreator<'ClearReceivedFlightLegDeleteStatus', any>(
    'ClearReceivedFlightLegDeleteStatus',
  ),
  SetFlightLegToDelete: new ActionCreator<'SetFlightLegToDelete', SetFlightLegToDeleteProps>('SetFlightLegToDelete'),
  DeleteLegConfirmation: new ActionCreator<'DeleteLegConfirmation', DeleteLegConfirmationProps>(
    'DeleteLegConfirmation',
  ),

  ClearRelevantFlightInfoFields: new ActionCreator<'ClearRelevantFlightInfoFields', any>(
    'ClearRelevantFlightInfoFields',
  ),

  SetSelectedOverlaidMissionPlanId: new ActionCreator<'SetSelectedOverlaidMissionPlanId', SetMissionDetailsOnMap>(
    'SetSelectedOverlaidMissionPlanId',
  ),
  SetSelectedOverlaidFlightLegId: new ActionCreator<'SetSelectedOverlaidFlightLegId', SetMissionDetailsOnMap>(
    'SetSelectedOverlaidFlightLegId',
  ),
  ClearMissionData: new ActionCreator<'ClearMissionData', MapAction>('ClearMissionData'),

  UpdateTakeoffTime: new ActionCreator<'UpdateTakeoffTime', UpdateFlightLegActionPayload>('UpdateTakeoffTime'),
  UpdateTakeoffTimeSuccess: new ActionCreator<'UpdateTakeoffTimeSuccess', any>('UpdateTakeoffTimeSuccess'),
  UpdateTakeoffTimeError: new ActionCreator<'UpdateTakeoffTimeError', string>('UpdateTakeoffTimeError'),
  ClearTakeoffTimeError: new ActionCreator<'ClearTakeoffTimeError', any>('ClearTakeoffTimeError'),
  removeFailedToParseFlightLeg: new ActionCreator<
    'removeFailedToParseFlightLeg',
    { fileName?: string; isClearAll?: boolean }
  >('removeFailedToParseFlightLeg'),
  UpdateFailedToParseFlightLegs: new ActionCreator<'UpdateFailedToParseFlightLegs', FlightLegUploadSuccessResponse[]>(
    'UpdateFailedToParseFlightLegs',
  ),
};

export type Action = typeof ActionCreators[keyof typeof ActionCreators];

export function getFlightLegs(missionPlanId: number, datapathUuid: string | undefined, userId: string) {
  return (dispatch: any) => {
    dispatch(ActionCreators.RequestFlightLegs.create({ missionPlanId, datapathUuid, userId }));
  };
}

export function clearReceivedFlightLegsError() {
  return (dispatch: any) => {
    dispatch(ActionCreators.ClearReceivedFlightLegsError.create());
  };
}

export function getFlightLegsInfo(missionPlanId: number, userId: string) {
  return ActionCreators.RequestFlightLegsInfo.create({ missionPlanId, userId });
}

export function clearFlightLegsInfo() {
  return ActionCreators.ClearFlightLegsInfo.create();
}

export function clearFlightLegsInfoSuccess() {
  return ActionCreators.ClearReceivedFlightLegsInfoSuccess.create();
}

export function clearFlightLegsInfoError() {
  return ActionCreators.ClearReceivedFlightLegsInfoError.create();
}

export function parseFlightLegFile(missionPlanId: number, userId: string, files: File[]) {
  return ActionCreators.RequestFlightLegUpload.create({ missionPlanId: missionPlanId, userId: userId, files: files });
}

export function clearFlightLegUploadError() {
  return ActionCreators.ClearReceivedFlightLegUploadError.create();
}

export function updateFlightLegsOrder(missionPlanId: number, userId: string, idList: string[]) {
  return ActionCreators.RequestFlightLegOrder.create({ missionPlanId: missionPlanId, userId: userId, idList: idList });
}

export function clearFlightLegsOrderError() {
  return ActionCreators.ClearReceivedFlightLegOrderError.create();
}

export function deleteFlightLeg(userId: string, flightLegId: number) {
  return ActionCreators.RequestFlightLegDelete.create({ userId: userId, flightLegId: flightLegId });
}

export function clearDeleteFlightLegStatus() {
  return ActionCreators.ClearReceivedFlightLegDeleteStatus.create();
}

export function showDeleteLegConfirmation(visible: boolean) {
  return ActionCreators.DeleteLegConfirmation.create({ visible: visible });
}

export function setFlightLegToDelete(flightLeg: FlightLegInfo | undefined) {
  return ActionCreators.SetFlightLegToDelete.create({ flightLeg: flightLeg });
}

export function clearFlightInfoFields() {
  return ActionCreators.ClearRelevantFlightInfoFields.create();
}

export function setSelectedOverlaidMissionPlanId(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return (missionPlanId: number) => {
    return ActionCreators.SetSelectedOverlaidMissionPlanId.create({
      mapId,
      id: missionPlanId,
    });
  };
}
export function setSelectedOverlaidFlightLegId(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return (flightLegId: number | undefined) => {
    return ActionCreators.SetSelectedOverlaidFlightLegId.create({
      mapId,
      id: flightLegId,
    });
  };
}
export function clearMissionData(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return () => {
    return ActionCreators.ClearMissionData.create({
      mapId,
    });
  };
}

export const flightLegMapWrapper = (mapId = GOOGLE_MAP_INSTANCE_KEY) => {
  return {
    setSelectedOverlaidMissionPlanId: setSelectedOverlaidMissionPlanId(mapId),
    setSelectedOverlaidFlightLegId: setSelectedOverlaidFlightLegId(mapId),
    clearMissionData: clearMissionData(mapId),
  };
};

export function updateTakeoffTime(payload: UpdateFlightLegActionPayload) {
  return ActionCreators.UpdateTakeoffTime.create(payload);
}

export function updateTakeoffTimeSuccess() {
  return ActionCreators.UpdateTakeoffTimeSuccess.create();
}
export function updateTakeoffTimeError(error: string) {
  return ActionCreators.UpdateTakeoffTimeSuccess.create(error);
}
export function clearTakeoffTimeError() {
  return ActionCreators.ClearTakeoffTimeError.create();
}

export function removeFailedToParseFlightLeg(fileName?: string, isClearAll?: boolean) {
  return ActionCreators.removeFailedToParseFlightLeg.create({ fileName, isClearAll });
}

export function updateFailedToParseFlightLegs(failedToParseFlightLegs: FlightLegUploadSuccessResponse[]) {
  return ActionCreators.UpdateFailedToParseFlightLegs.create(failedToParseFlightLegs);
}